import type { ReactNode } from "react";
import { Card, DataTable, type DataTableProps, type DataTableState } from "@h2j-data-team/ui";
import { tableMessages } from "@/lib/ui-labels";
import { useTableDensity } from "@/lib/use-table-density";

// A list that is only shown: no search, sort or pages, so the table state never changes.
const STATE: DataTableState = { page: 1, pageSize: 1000, query: "", filters: {}, selection: [] };
const IGNORE = () => {};

/**
 * A DataTable for the lists that are only read — the findings and the compare
 * changes. The app's density preference applies. `empty` is the empty state; a
 * plain string is only a one-line note in place of the table, which these short
 * lists are better off with than a whole empty table.
 */
export function ReadOnlyTable<T>({
  empty,
  ...props
}: { empty: { title: string; description?: ReactNode } | string } & Omit<
  DataTableProps<T>,
  "state" | "onStateChange" | "messages" | "pagination" | "search" | "density"
>) {
  const { density } = useTableDensity();
  if (typeof empty === "string" && props.rows.length === 0 && !props.loading && !props.error) {
    return <Card className="px-5 py-6 text-sm text-dim">{empty}</Card>;
  }
  return (
    <DataTable<T>
      {...props}
      density={density}
      state={STATE}
      onStateChange={IGNORE}
      messages={tableMessages({ empty: typeof empty === "string" ? { title: empty } : empty })}
    />
  );
}
